import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import isEmptyUtil from '@utils/isEmpty';
import { NTA_COLORS } from '../constants';
import { ntaPageCss, ntaQuestionContentCss } from '../typography';
import NtaNumberPad from './NtaNumberPad';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0 0 16px;
  width: 100%;
  box-sizing: border-box;
  ${ntaPageCss}
`;

const Label = styled.p`
  margin: 0;
  ${ntaQuestionContentCss}
`;

const InputField = styled.div`
  width: 100%;
  min-height: 40px;
  padding: 10px 20px;
  box-sizing: border-box;
  border: 1px solid ${NTA_COLORS.divider};
  border-radius: 4px;
  background: ${NTA_COLORS.white};
  color: ${NTA_COLORS.textPrimary};
  font-size: 16px;
  line-height: 20px;

  &[data-empty='true'] {
    color: ${NTA_COLORS.resultLabel};
    font-size: 12px;
    line-height: 20px;
  }
`;

const getValidators = (allowNegative = false, allowFloat = false) => {
  if (!allowNegative && !allowFloat) {
    return /^([0-9]*)$/;
  }

  if (!allowNegative && allowFloat) {
    return /^[0-9]+([.][0-9]*)?$/;
  }

  return /^-?((0|([0-9]\d{0,}))(\.\d{0,})?)?$/;
};

const NtaIntegerAnswer = ({
  value,
  onChange,
  disabled,
  allowNegative,
  allowFloat
}) => {
  const validator = useMemo(
    () => getValidators(allowNegative, allowFloat),
    [allowFloat, allowNegative]
  );

  const [displayValue, setDisplayValue] = useState(() =>
    value == null || value === '' ? '' : String(value)
  );

  useEffect(() => {
    setDisplayValue(value == null || value === '' ? '' : String(value));
  }, [value]);

  const commitValue = (next) => {
    if (next.length > 16) {
      return;
    }

    if (!next) {
      setDisplayValue('');
      onChange('');
      return;
    }

    if (!validator.test(next)) {
      return;
    }

    setDisplayValue(next);

    const lastChar = next[next.length - 1];
    if (lastChar === '.' || next === '-') {
      return;
    }

    const parsed = Number(next);
    if (!Number.isNaN(parsed)) {
      onChange(parsed);
    }
  };

  const handleKeyPress = (key) => {
    if (disabled) {
      return;
    }

    if (key === '-') {
      if (!allowNegative) {
        return;
      }

      if (displayValue.startsWith('-')) {
        commitValue(displayValue.slice(1));
      } else {
        commitValue(`-${displayValue}`);
      }
      return;
    }

    if (key === '.') {
      if (!allowFloat || displayValue.includes('.')) {
        return;
      }

      commitValue(displayValue.length ? `${displayValue}.` : '0.');
      return;
    }

    commitValue(`${displayValue}${key}`);
  };

  const handleBackspace = () => {
    if (disabled) {
      return;
    }

    commitValue(displayValue.slice(0, -1));
  };

  const isEmpty = isEmptyUtil(displayValue);

  return (
    <Wrap data-testid="question-options">
      <Label>Write the correct integer</Label>
      <InputField
        role="textbox"
        aria-readonly="true"
        aria-label="Answer input"
        data-empty={isEmpty}
      >
        {isEmpty ? 'Type your answer here' : displayValue}
      </InputField>
      <NtaNumberPad
        disabled={disabled}
        onKeyPress={handleKeyPress}
        onBackspace={handleBackspace}
      />
    </Wrap>
  );
};

NtaIntegerAnswer.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  allowNegative: PropTypes.bool,
  allowFloat: PropTypes.bool
};

NtaIntegerAnswer.defaultProps = {
  value: '',
  disabled: false,
  allowNegative: true,
  allowFloat: true
};

export default NtaIntegerAnswer;
